import { PUBLIC_DO_ASSETS_URL, PUBLIC_STORE_ID } from '$env/static/public';

import { redirect } from '@sveltejs/kit';

import { AuctionStatus, MAX_LIMIT, Status } from '@do/models';

import { getPaginatedSeoData, seoConfig } from '@shared/configs';
import { extendBreadcrumbsWithSlug } from '@shared/ui/breadcrumbs';
import { parseSlug } from '@shared/utils';

import { ArtAssetCardApi } from '@entities/art-asset';
import { mapDataToIU } from '@entities/artist';
import { ArtistApi } from '@entities/artist';
import { AuctionApi } from '@entities/auction';
import { slugLibs } from '@entities/slug';

import { ARTWORKS_PER_PAGE } from '@pages/artworks';

import type { PageLoad } from './$types';

export const ssr = true;

export const load: PageLoad = async ({ data, fetch, params }) => {
  const { form, id, slug, artworksLoadMore, artworksSortColumn, artworksSortOrder, extraPath, filters } = data;

  const artistApi = new ArtistApi(fetch);
  const auctionApi = new AuctionApi(fetch);
  const artworksApi = new ArtAssetCardApi(fetch);

  const auctionsParams = new URLSearchParams();
  auctionsParams.append('offset', '0');
  auctionsParams.append('limit', MAX_LIMIT.toString());
  auctionsParams.append('institution_id', PUBLIC_STORE_ID);
  auctionsParams.append('artists', id);
  auctionsParams.append('statuses', `${AuctionStatus.Active},${AuctionStatus.Finalizing}`);

  const parsedParams = parseSlug(slug);

  const artworksPage = +parsedParams.page > 0 ? +parsedParams.page : 1; // get only number from page param

  const [artist, { artworks, totalCount: artworksCount }, { auctions, auctionsCount }] = await Promise.all([
    artistApi.readOne(id).then((response) => {
      if (response.status !== Status.Published) {
        throw redirect(302, '/artists');
      }
      return mapDataToIU(response);
    }),
    artworksApi.readMany({
      ...parsedParams,
      page: artworksPage,
      loadMore: artworksLoadMore,
      perPage: ARTWORKS_PER_PAGE,
      offset: (artworksPage - 1) * ARTWORKS_PER_PAGE,
      institutionId: Number(PUBLIC_STORE_ID),
      artists: id,
      sort: artworksSortColumn,
      order: artworksSortOrder,
    }),
    auctionApi.readMany(auctionsParams),
  ]);

  slugLibs.handleSlugMismatch({
    currentSlug: params.name,
    entitySlug: artist.slug,
    redirectPath: `/artist-${artist.id}-${artist.slug}/`,
  });

  const defaultPreset = artist.photo?.asset_transformations?.find(
    (preset) => preset.preset_key === 'default-1500w-webp' || preset.preset_key === 'default-500w-webp',
  );

  const baseBreadcrumbs = [
    {
      name: 'Home',
      slug: '/',
      canonical: false,
    },
    {
      name: 'Artists',
      slug: '/artists/',
      canonical: false,
    },
    {
      name: artist.name,
      slug: `/artist-${artist.id}-${artist.slug}/`,
      canonical: !extraPath,
    },
  ];

  const breadcrumbs = extendBreadcrumbsWithSlug({
    baseBreadcrumbs,
    name: `Page ${artworksPage}`,
    slug: extraPath,
  });

  const seoForPaginatedPage = getPaginatedSeoData({
    page: artworksPage,
    title: `${artist.name}: Digitized Art in the Digital Original Format for Sale in the Ukrainian Fund of Digitized Art (UFDA) Store`,
    metaDescription: `Explore the vibrant art of ${artist.name} ✓ Find ${artist.name} artworks available for purchase in the Ukrainian Fund of Digitized Art (UFDA) ✓ Discover unique digital originals ✓ Convert ${artist.name} art into digital originals`,
  });

  const seo = seoConfig.artist({
    artistName: artist.name,
    metaImage: {
      alt: artist.name,
      caption: artist.name,
      url: `${PUBLIC_DO_ASSETS_URL}/${defaultPreset?.transformation_name}`,
      width: artist.photo?.width ?? 0,
      height: artist.photo?.height ?? 0,
    },
    breadcrumbs,
    robots: !filters,
    ...seoForPaginatedPage,
  });

  return {
    artist,
    auctions,
    artworks,
    artworksCount,
    artworksPage,
    artworksLoadMore,
    auctionsCount,
    form,
    seo,
    hasParams: !!slug,
  };
};
